import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserAdmin } from 'src/app/core/models/admin';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  DRIVER_LOAD_REPORT_LABELS,
  DriverLoadFormGroup,
  REPORT_FILE_NAME,
} from './driver-load-report.constants';

@Component({
  selector: 'app-driver-load-report',
  templateUrl: './driver-load-report.component.html',
  styleUrls: ['./driver-load-report.component.scss'],
  standalone: false,
})
export class DriverLoadReportComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: DriverLoadFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<DriverLoadReportComponent> = inject(
    MatDialogRef<DriverLoadReportComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private onRoadOperationService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly CONSTS = DRIVER_LOAD_REPORT_LABELS;
  readonly IMAGES = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  driverLoadForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  routes = MOCK_ROUTES;
  currentUser!: UserAdmin | undefined;
  locationCode = '';

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.locationCode = user?.location;
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.driverLoadForm = this.fb.group({
            date: [this.currDate],
            route: [this.routes[0]],
            reportFormat: [Options.CSV],
          });

          return this.getRouteList();
        }),
      )
      .subscribe();
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof DriverLoadReportComponent
   */
  private getRouteList(): Observable<void> {
    return this.onRoadOperationService
      .getDriverLoadRoutes(this.locationCode)
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.dLRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: STATUS_OPTIONS.ALL_SELECTED,
            value: 'All',
          });
        }),
      );
  }
  onExit() {
    this.commonUtils.openExitModal(this.driverLoadForm.dirty);
  }
  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof DeliveryManifestComponent
   */
  getReport() {
    const formValue = this.driverLoadForm.value;
    const date = this.commonUtils.getFormattedDate(formValue.date);
    const request = {
      locationCode: this.locationCode,
      date,
      loop:
        formValue.route?.value === STATUS_OPTIONS.ALL_SELECTED
          ? STATUS_OPTIONS.ALL_SELECTED
          : formValue.route,
    };
    this.onRoadOperationService
      .getDriverLoadReport(request)
      .pipe(take(1))
      .subscribe((data) => {
        if (data.response.driverLoads.length) {
          const customHeader = [['', '', '', 'DRIVER LOAD REPORT', '', '', '']];
          this.reportUtils.exportCSVReport(
            data.response.driverLoads,
            REPORT_FILE_NAME,
            customHeader,
          );
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }
}
